/**
 * At-Home (1:1 classes at the child's home) — API surface, wired to
 * `/api/v1/oh_at_home/*`. Not related to At Apartment (`api/apartment.js`).
 *
 * admin/*   Alfred → At Home
 * teacher/* Alfred → Teacher → At Home (backend scopes to the logged-in teacher)
 */
import { apiEndpoints, apiURLOrigin } from 'api/common'
import { get, patch, post, put, remove } from 'request'
import { sentryLogger } from 'sentry'

// Lazy: api/common sits in a circular import with the api barrel.
const base = () => apiEndpoints.atHome

const rows = (res) =>
  Array.isArray(res?.data) ? res.data : res?.data?.results || []

const qs = (params = {}) => {
  const clean = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ''
  )
  return clean.length ? `?${new URLSearchParams(clean).toString()}` : ''
}

const wrap =
  (name, fn) =>
  async (...args) => {
    try {
      return await fn(...args)
    } catch (error) {
      sentryLogger(error, name)
      throw error
    }
  }

// --- reference data ----------------------------------------------------------
export const fetchAtHomeCategories = wrap('fetchAtHomeCategories', async () =>
  rows(await get(`${base()}/admin/categories/`))
)
export const createAtHomeCategory = wrap('createAtHomeCategory', (data) =>
  post(`${base()}/admin/categories/`, data)
)
export const updateAtHomeCategory = wrap('updateAtHomeCategory', (uuid, data) =>
  patch(`${base()}/admin/categories/${uuid}/`, data)
)

// The static pages the parent app can render, served by the backend so this
// dropdown and the serializer's validator read the same list.
export const fetchAtHomeCategoryPages = wrap(
  'fetchAtHomeCategoryPages',
  async () => rows(await get(`${base()}/admin/category-pages/`))
)

export const fetchAtHomeApartments = wrap('fetchAtHomeApartments', async () =>
  rows(await get(`${base()}/admin/apartments/`))
)
export const createAtHomeApartment = wrap('createAtHomeApartment', (data) =>
  post(`${base()}/admin/apartments/`, data)
)
export const updateAtHomeApartment = wrap(
  'updateAtHomeApartment',
  (uuid, data) => patch(`${base()}/admin/apartments/${uuid}/`, data)
)
// Which categories an apartment offers (Setup → Apartments → Categories).
export const fetchAtHomeApartmentCategories = wrap(
  'fetchAtHomeApartmentCategories',
  async (uuid) =>
    rows(await get(`${base()}/admin/apartments/${uuid}/categories/`))
)
export const setAtHomeApartmentCategory = wrap(
  'setAtHomeApartmentCategory',
  async (uuid, category, active) =>
    rows(
      await put(`${base()}/admin/apartments/${uuid}/categories/`, {
        category,
        active
      })
    )
)
export const deleteAtHomeApartment = wrap('deleteAtHomeApartment', (uuid) =>
  remove(`${base()}/admin/apartments/${uuid}/`)
)

export const fetchAtHomeTeachers = wrap('fetchAtHomeTeachers', async (q) =>
  rows(await get(`${base()}/admin/teachers/${qs({ q })}`))
)
export const searchAtHomeParents = wrap('searchAtHomeParents', async (q) =>
  rows(await get(`${base()}/admin/parents/search/${qs({ q })}`))
)

// --- slots ------------------------------------------------------------------
// params: { status: open|booked|cancelled|all, kind, teacher, apartment, category }
// range:  { from, to } — the server's own date window, inclusive YYYY-MM-DD on
// the slot's date. Its own argument so a page can hand over the header date
// picker's value untouched; empty ends are dropped by qs().
export const fetchAtHomeSlots = wrap(
  'fetchAtHomeSlots',
  async (params, range) =>
    rows(await get(`${base()}/admin/slots/${qs({ ...params, ...range })}`))
)
export const createAtHomeSlot = wrap('createAtHomeSlot', (data) =>
  post(`${base()}/admin/slots/`, data)
)
export const updateAtHomeSlot = wrap('updateAtHomeSlot', (uuid, data) =>
  patch(`${base()}/admin/slots/${uuid}/`, data)
)
export const withdrawAtHomeSlot = wrap('withdrawAtHomeSlot', (uuid) =>
  remove(`${base()}/admin/slots/${uuid}/`)
)

// --- bookings ---------------------------------------------------------------
// params: { view: booked|attended|cancelled|all, teacher, apartment, category }
// range:  { from, to } — as for slots, inclusive bounds on the session's date.
export const fetchAtHomeBookings = wrap(
  'fetchAtHomeBookings',
  async (params, range) =>
    rows(await get(`${base()}/admin/bookings/${qs({ ...params, ...range })}`))
)
// "Cancelled & rescheduled" log: one row per change, newest first. A cancelled
// module is one row. params: { type, kind, teacher }; range: { from, to } on
// when the change was made.
export const fetchAtHomeChanges = wrap(
  'fetchAtHomeChanges',
  async (params, range) =>
    rows(await get(`${base()}/admin/changes/${qs({ ...params, ...range })}`))
)
// { slot, parent, child, adult_presence_acknowledged, payment_status, payment_reference }
export const createAtHomeBooking = wrap('createAtHomeBooking', (data) =>
  post(`${base()}/admin/bookings/`, data)
)
export const cancelAtHomeBooking = wrap('cancelAtHomeBooking', (uuid, reason) =>
  patch(`${base()}/admin/bookings/${uuid}/`, { action: 'cancel', reason })
)
// Move a booking to another open slot of the same category (old slot reopens).
export const rescheduleAtHomeBooking = wrap(
  'rescheduleAtHomeBooking',
  (uuid, slot, reason) =>
    patch(`${base()}/admin/bookings/${uuid}/`, {
      action: 'reschedule',
      slot,
      reason
    })
)
// Send the Razorpay payment link again on WhatsApp (pay-by-link bookings only).
export const resendAtHomePaymentLink = wrap('resendAtHomePaymentLink', (uuid) =>
  patch(`${base()}/admin/bookings/${uuid}/`, { action: 'resend_link' })
)
// Module session: times the same teacher could take it instead (next 3 weeks)
export const fetchAtHomeRescheduleOptions = wrap(
  'fetchAtHomeRescheduleOptions',
  async (uuid) =>
    (await get(`${base()}/admin/bookings/${uuid}/reschedule-options/`)).data
)
// Module session: move to { date, start_time, reason } with the same teacher
export const rescheduleAtHomeSession = wrap(
  'rescheduleAtHomeSession',
  (uuid, data) =>
    patch(`${base()}/admin/bookings/${uuid}/`, {
      action: 'reschedule',
      ...data
    })
)

// Ops closing a session the teacher did not close on its day: attended or
// no-show, with notes. Refused by the server for today's/future sessions.
export const closeAtHomeBooking = wrap(
  'closeAtHomeBooking',
  (uuid, attended, notes) =>
    patch(`${base()}/admin/bookings/${uuid}/`, {
      action: 'close',
      attended,
      notes
    })
)

// Session notes on their own, before or after the session is closed.
export const updateAtHomeBookingNotes = wrap(
  'updateAtHomeBookingNotes',
  (uuid, notes) =>
    patch(`${base()}/admin/bookings/${uuid}/`, { action: 'notes', notes })
)

export const markAtHomeBookingPaid = wrap(
  'markAtHomeBookingPaid',
  (uuid, reference, amount) =>
    patch(`${base()}/admin/bookings/${uuid}/`, {
      action: 'mark_paid',
      reference,
      amount
    })
)

// --- module plans (CategoryStyle.MODULE) ------------------------------------
export const fetchAtHomePricing = wrap('fetchAtHomePricing', async () =>
  rows(await get(`${base()}/admin/pricing/`))
)
export const createAtHomePricing = wrap('createAtHomePricing', (data) =>
  post(`${base()}/admin/pricing/`, data)
)
export const updateAtHomePricing = wrap('updateAtHomePricing', (uuid, data) =>
  patch(`${base()}/admin/pricing/${uuid}/`, data)
)
export const deleteAtHomePricing = wrap('deleteAtHomePricing', (uuid) =>
  remove(`${base()}/admin/pricing/${uuid}/`)
)

// Named modules inside a category (Setup → Modules). params: { category, status }
export const fetchAtHomeModules = wrap('fetchAtHomeModules', async (params) =>
  rows(await get(`${base()}/admin/modules/${qs(params)}`))
)
export const createAtHomeModule = wrap('createAtHomeModule', (data) =>
  post(`${base()}/admin/modules/`, data)
)
export const updateAtHomeModule = wrap('updateAtHomeModule', (uuid, data) =>
  patch(`${base()}/admin/modules/${uuid}/`, data)
)
export const deleteAtHomeModule = wrap('deleteAtHomeModule', (uuid) =>
  remove(`${base()}/admin/modules/${uuid}/`)
)

// params: { category, teacher, active }
export const fetchAtHomeListings = wrap('fetchAtHomeListings', async (params) =>
  rows(await get(`${base()}/admin/listings/${qs(params)}`))
)
export const createAtHomeListing = wrap('createAtHomeListing', (data) =>
  post(`${base()}/admin/listings/`, data)
)
export const updateAtHomeListing = wrap('updateAtHomeListing', (uuid, data) =>
  patch(`${base()}/admin/listings/${uuid}/`, data)
)
export const deleteAtHomeListing = wrap('deleteAtHomeListing', (uuid) =>
  remove(`${base()}/admin/listings/${uuid}/`)
)

// { listings: [uuid, uuid?], module, grade, start_date } → the 8 sessions + price before committing
export const previewAtHomePlan = wrap(
  'previewAtHomePlan',
  async (data) => (await post(`${base()}/admin/plans/preview/`, data)).data
)
// params: { status: booked|cancelled|all }
export const fetchAtHomePlans = wrap('fetchAtHomePlans', async (params) =>
  rows(await get(`${base()}/admin/plans/${qs(params)}`))
)
// { listings, module, parent, child, grade, start_date, adult_presence_acknowledged } — pay-by-link by default
export const createAtHomePlan = wrap('createAtHomePlan', (data) =>
  post(`${base()}/admin/plans/`, data)
)
export const cancelAtHomePlan = wrap('cancelAtHomePlan', (uuid, reason) =>
  patch(`${base()}/admin/plans/${uuid}/`, { action: 'cancel', reason })
)
export const markAtHomePlanPaid = wrap(
  'markAtHomePlanPaid',
  (uuid, reference) =>
    patch(`${base()}/admin/plans/${uuid}/`, { action: 'mark_paid', reference })
)
// Monthly plan: book the next month (new plan from the day after this one ends)
export const renewAtHomePlan = wrap('renewAtHomePlan', (uuid) =>
  patch(`${base()}/admin/plans/${uuid}/`, { action: 'renew' })
)
export const resendAtHomePlanLink = wrap('resendAtHomePlanLink', (uuid) =>
  patch(`${base()}/admin/plans/${uuid}/`, { action: 'resend_link' })
)

// --- issues -----------------------------------------------------------------
export const fetchAtHomeIssues = wrap(
  'fetchAtHomeIssues',
  async (status = 'open') =>
    rows(await get(`${base()}/admin/issues/${qs({ status })}`))
)
export const resolveAtHomeIssue = wrap(
  'resolveAtHomeIssue',
  (uuid, resolution_notes) =>
    post(`${base()}/admin/issues/${uuid}/resolve/`, { resolution_notes })
)

// --- teacher ----------------------------------------------------------------
export const fetchMyAtHomeSlots = wrap(
  'fetchMyAtHomeSlots',
  async (status = 'open', from) =>
    rows(await get(`${base()}/teacher/slots/${qs({ status, from })}`))
)
export const fetchMyAtHomeSessions = wrap(
  'fetchMyAtHomeSessions',
  async (view = 'upcoming') =>
    rows(await get(`${base()}/teacher/sessions/${qs({ view })}`))
)
// action: arrive | start | complete ({attended, notes}) | notes ({notes})
// Session photo for the parent's WhatsApp summary: the same oh_files upload the
// teacher app uses, into the oh_at_home folder (public URL Meta can fetch).
export const uploadAtHomeSessionPhoto = wrap(
  'uploadAtHomeSessionPhoto',
  async (file) => {
    const form = new FormData()
    form.append('file', file)
    form.append('file_type', 'image')
    form.append('app', 'oh_at_home')
    const res = await post(`${apiURLOrigin}/oh_files/upload_file`, form)
    const url = res?.data?.data?.url
    if (!url) throw new Error('Upload failed')
    return url
  }
)

export const atHomeSessionAction = wrap('atHomeSessionAction', (uuid, body) =>
  post(`${base()}/teacher/sessions/${uuid}/`, body)
)
export const fetchMyAtHomeIssues = wrap('fetchMyAtHomeIssues', async () =>
  rows(await get(`${base()}/teacher/issues/`))
)
export const raiseAtHomeIssue = wrap('raiseAtHomeIssue', (data) =>
  post(`${base()}/teacher/issues/`, data)
)

/**
 * The WhatsApp number behind the help icon in the parent app.
 *
 * GET returns the number in force plus `editable`, which is the backend's own
 * answer to "is this user a super admin" -- the UI disables the field from
 * that rather than re-deriving the role check in the browser, so the two
 * cannot disagree. PUT is refused with 403 for anyone else.
 */
// `.data`, like every other reader in this file. Returning the raw axios
// response left `editable` and `whatsapp_number` undefined on the object the
// screen reads, so the field came back EMPTY and DISABLED for everyone --
// super admins included, because the screen takes the server's answer rather
// than re-deriving the role in the browser. The backend was right all along.
export const fetchAtHomeSupportContact = wrap(
  'fetchAtHomeSupportContact',
  async () => (await get(`${base()}/admin/support-contact/`)).data
)
export const updateAtHomeSupportContact = wrap(
  'updateAtHomeSupportContact',
  // A key that is left out is not changed server-side, so the number and the
  // greeting can be saved independently.
  async (payload) =>
    (await put(`${base()}/admin/support-contact/`, payload)).data
)
